import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import _get from 'lodash/get'
import Toast from '@ntuctech/devex-tangram/Toast'
import SvgAlert from '@ntuctech/devex-tangram/Icon/SvgAlert'
import { Skeleton } from '@ntuctech/devex-tangram'

import { usePrevious } from '../../../lib/utils'
import { getUserType } from '../../../utils/userType'
import QuickBuyHome from './QuickBuyHome'
import AddGovernmentAgency from './AddGovernmentAgency'
import ChangeTimeSlotPaymentMethodPopup from './ChangeTimeSlotPaymentMethodPopup'
import { get, post, headers } from '../../../lib/fetch'
import {
  API_INTERVAL,
  EXPIRY_DURATION,
} from '../../../utils/constants/payments'
import EmailVerified from '../../Payment/EmailVerified'

const SkeletonLoader = () => (
  <div style={{ display: 'flex', maxWidth: '100%', flexDirection: 'column' }}>
    <Skeleton variant="rectangle" height={32} />
    <Skeleton
      variant="rectangle"
      height={72}
      style={{ marginTop: '1rem', marginBottom: '1.5rem' }}
    />

    <Skeleton
      variant="rectangle"
      height={48}
      width={122}
      style={{ alignSelf: 'end' }}
    />
  </div>
)

const CorpBillingPayment = props => {
  const {
    refreshAgencyList,
    agenciesList,
    validateTimeSlot,
    onSubmit,
    resetAPICalled,
    resetTabValue,
    values = [],
  } = props
  const [requestExpired, setRequestExpired] = useState(false)
  const [userEmail, setUserEmail] = useState('')
  const [errorMsg, setErrorMsg] = useState('')
  const [userEmailRejected, setUserEmailRejected] = useState(false)
  const [showErrorMessage, setShowErrorMessage] = useState(false)
  const [addGovAgency, setAddGovAgency] = useState(false)
  const [emailVerified, setEmailVerified] = useState(false)
  const [showAlreadyAddedAgencies, setShowAlreadyAddedAgencies] = useState(true)
  const [isValidTimeSlotSelected, setIsValidTimeSlotSelected] = useState(0)
  const [openChangeTimeSlotPopup, setOpenChangeTimeSlotPopup] = useState(false)
  const initialFormData = {
    govAgencyEmail: userEmail,
  }
  const previousTimeslot = usePrevious(JSON.stringify(values))

  useEffect(() => {
    const isB2BUser = getUserType() === 'B2B'
    if (
      (isB2BUser && isValidTimeSlotSelected === 0) ||
      previousTimeslot !== JSON.stringify(values)
    ) {
      setIsValidTimeSlotSelected(0)
      validateTimeSlot().then(response => {
        if (response === 1) {
          setIsValidTimeSlotSelected(1)
        } else if (response === 0) {
          setIsValidTimeSlotSelected(2)
          setOpenChangeTimeSlotPopup(true)
        }
      })
    }
  }, [
    isValidTimeSlotSelected,
    previousTimeslot,
    props.values?.sellerTimeSlotsString,
    validateTimeSlot,
    values,
  ])

  const isEmailApprovedRejected = React.useCallback(
    async (emailId, timerId) => {
      setErrorMsg('')
      const response = await get(`corp-billings?buyercorpemail=${emailId}`, {
        headers: headers(),
      })
      const agencies = _get(response, 'data.corpBillings')
      if (agencies && agencies.length > 0) {
        if (agencies[0].status === 'APPROVED') {
          setShowAlreadyAddedAgencies(true)
          setUserEmailRejected(false)
          refreshAgencyList()
          setAddGovAgency(false)
          setShowErrorMessage(false)
          setEmailVerified(false)
          setUserEmail('')
          clearInterval(timerId)
        } else if (agencies[0].approvalMessage === 'MIDDLEWARE_REJECTED') {
          setRequestExpired(false)
          setUserEmailRejected(true)
          setAddGovAgency(true)
          setShowErrorMessage(true)
          setEmailVerified(false)
          clearInterval(timerId)
        } else if (agencies[0].approvalMessage === 'REQUEST_EXPIRED') {
          setRequestExpired(true)
          setUserEmailRejected(false)
          setAddGovAgency(true)
          setShowErrorMessage(true)
          setEmailVerified(false)
          clearInterval(timerId)
        }
      }
    },
    [refreshAgencyList]
  )

  const handleAddGovAgency = React.useCallback(() => {
    setErrorMsg('')
    setAddGovAgency(true)
    setEmailVerified(false)
    setShowAlreadyAddedAgencies(false)
    setShowErrorMessage(false)
  }, [])

  const handleVerifyEmail = React.useCallback(
    async emailId => {
      setErrorMsg('')
      const params = {
        buyerCorpEmail: emailId,
      }
      const response = await post(`corp-billings`, {
        headers: headers(),
        body: JSON.stringify(params),
      })
        .catch(error => {
          setErrorMsg(error.message.split('says:')[1])
          return null
        })
        .finally(() => {
          setUserEmail(emailId)
        })
      if (response && response.code === 200) {
        setEmailVerified(true)
        //Code to call api every 10 sec till 2 mins START
        const timerId = setInterval(() => {
          isEmailApprovedRejected(emailId, timerId, null)
        }, API_INTERVAL)
        const timeoutTime = setTimeout(() => {
          clearInterval(timerId)
          isEmailApprovedRejected(emailId, timerId, timeoutTime)
        }, EXPIRY_DURATION)
      }
    },
    [isEmailApprovedRejected]
  )

  const handleChangeTimeSlot = () => {
    resetAPICalled()
    onSubmit({}, null)
    setOpenChangeTimeSlotPopup(false)
  }

  const handleChangePaymentMethod = React.useCallback(() => {
    setOpenChangeTimeSlotPopup(false)
    resetTabValue()
  }, [resetTabValue])

  const handleExpiredVerification = () => {
    setRequestExpired(true)
    setUserEmailRejected(false)
    setAddGovAgency(true)
    setShowErrorMessage(true)
    setEmailVerified(false)
  }

  return (
    <div>
      {errorMsg && (
        <Toast inline type="alert" icon={<SvgAlert />} message={errorMsg} />
      )}

      {isValidTimeSlotSelected !== 1 && <SkeletonLoader />}

      {isValidTimeSlotSelected === 1 &&
        !addGovAgency &&
        showAlreadyAddedAgencies &&
        Boolean(agenciesList.length) && (
          <QuickBuyHome
            onAddGovAgency={handleAddGovAgency}
            agenciesList={agenciesList}
            hasCorpBillingSetup={Boolean(agenciesList.length)}
            onSubmit={onSubmit}
          />
        )}
      {isValidTimeSlotSelected === 1 &&
        ((addGovAgency && !emailVerified && !showAlreadyAddedAgencies) ||
          (!emailVerified &&
            showAlreadyAddedAgencies &&
            !agenciesList.length)) && (
          <AddGovernmentAgency
            onVerifyEmail={handleVerifyEmail}
            toggleErrorMessage={showErrorMessage}
            userEmail={userEmail}
            initialFormData={initialFormData}
            requestCancelled={userEmailRejected}
            requestExpired={requestExpired}
          />
        )}
      {isValidTimeSlotSelected === 1 && emailVerified && (
        <div style={{ paddingBottom: '0.75rem' }}>
          <EmailVerified onClose={handleExpiredVerification} />
        </div>
      )}
      {openChangeTimeSlotPopup && (
        <ChangeTimeSlotPaymentMethodPopup
          open={openChangeTimeSlotPopup}
          onChangeTimeSlot={handleChangeTimeSlot}
          onChangePaymentMethod={handleChangePaymentMethod}
        />
      )}
    </div>
  )
}

CorpBillingPayment.propTypes = {
  refreshAgencyList: PropTypes.func.isRequired,
  agenciesList: PropTypes.array.isRequired,
  validateTimeSlot: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  resetAPICalled: PropTypes.func.isRequired,
  resetTabValue: PropTypes.func.isRequired,
  values: PropTypes.array.isRequired,
}

export default CorpBillingPayment
